package com.essntl.features.task.ui.alltasks

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.utils.update
import com.essntl.core.utils.viewmodel.*
import com.essntl.features.category.domain.repository.CategoryRepository
import com.essntl.features.itinerary.domain.repository.ItineraryRepository
import com.essntl.features.profile.domain.repository.ProfileRepository
import com.essntl.features.task.domain.model.TaskFilterOptions
import com.essntl.features.task.domain.model.TaskModel
import com.essntl.features.task.domain.repository.TaskRepository
import com.essntl.features.task.domain.use_case.UpdateTaskUseCase
import com.essntl.features.task.ui.sharedtask.SharedTaskViewModel
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate

class AllTasksViewModel(
    private val taskRepository: TaskRepository,
    private val categoryRepository: CategoryRepository,
    private val profileRepository: ProfileRepository,
    private val updateTaskUseCase: UpdateTaskUseCase,
    private val state: MutableStateFlow<AllTasksState>,
    private val itineraryRepository: ItineraryRepository,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
) : ViewModel {

    private val getNextTaskPageJobList = mutableListOf<Job>()

    private val sharedTaskViewModel =
        SharedTaskViewModel(
            itineraryRepository = itineraryRepository,
            scope = scope,
            loadingManager = loadingManager,
        )

    init {
        getOverdueTasks()
        getNextTaskPage()

        getAllCategory()
        getAllProfile()

        collectSharedTaskState()

        // Update task Use Case
        collectLoading(
            flow = updateTaskUseCase.state.map { it.loadingSet },
            setState = { state.update { s -> s.copy(updateTaskLoading = it) } },
        )

        collectPaginationSuccessEvent(
            flow = updateTaskUseCase.successEvent,
            update = { old, new -> new.copy(itineraryService = old.itineraryService) },
            predicate = { first, second -> first.id == second.id },
            getState = { state.value.getAllTasks },
            setState = { s -> state.update { it.copy(getAllTasks = s) } },
        )
    }

    private var getSharedTaskStateJob: Job? = null
    private fun collectSharedTaskState() {
        // collecting sharedTaskState
        getSharedTaskStateJob?.cancel()
        getSharedTaskStateJob = scope.launch {
            sharedTaskViewModel.state.collect { data ->
                state.update {
                    it.copy(
                        sharedTaskState = data,
                    )
                }
            }
        }
    }

    fun onEvent(event: AllTasksEvent) {
        when (event) {
            is AllTasksEvent.LoadTasks ->
                getNextTaskPage(event.refresh)

            is AllTasksEvent.AddEditTask ->
                addEditTask(event)

            is AllTasksEvent.FilterTasks ->
                getAllTasksByDate(event)

            is AllTasksEvent.UpdateTaskStatus ->
                updateTaskStatus(event)

            is AllTasksEvent.EditClient ->
                editClient(event)

            is AllTasksEvent.UpdateTaskOwner ->
                updateTaskOwner(event)
        }
    }

    private fun getOverdueTasks() =
        getData(
            getData = { taskRepository.getOverdueTasks() },
            getState = { state.value.getAllOverDueTasks },
            setState = { newState -> state.update { it.copy(getAllOverDueTasks = newState) } },
            onSuccess = { tasksList ->
                updateSharedState(tasksList)
            },
        )

    private fun getNextTaskPage(refresh: Boolean = false) {
        if (refresh)
            state.update {
                it.copy(
                    getAllTasks = PaginationState(),
                )
            }

        getPaginationData(
            getData = {
                val nextPage = state.value.getAllTasks.loadedPages + 1L
                taskRepository.getAll(
                    page = nextPage,
                    filterOptions = state.value.filterOptions,
                )
            },
            getState = { state.value.getAllTasks },
            setState = { newState ->
                state.update {
                    it.copy(
                        getAllTasks = newState,
                    )
                }
            },
            onSuccess = { _, tasksList ->
                updateSharedState(tasksList)
            },
        ).also { job ->
            try {
                getNextTaskPageJobList.add(job)
                job.invokeOnCompletion {
                    getNextTaskPageJobList.removeAll { it.isCompleted }
                }
            } catch (e: Exception) {
                e.printStackTrace()
            }
        }
    }

    private fun updateSharedState(tasksList: List<TaskModel>) {
        val itineraryProposalServiceIds = tasksList
            .mapNotNull { it.itineraryService?.itineraryId }
            .filter { id -> state.value.sharedTaskState.getAllItineraries.data.orEmpty().none { id == it.id } }

        val itineraryTaskIds = tasksList
            .mapNotNull { it.itinerary?.id }
            .filter { id -> state.value.sharedTaskState.getAllItineraries.data.orEmpty().none { id == it.id } }

        val itineraryIds = (itineraryProposalServiceIds + itineraryTaskIds).toSet()

        if (itineraryIds.isNotEmpty())
            sharedTaskViewModel.getAllItineraries(itineraryIds.toSet())
    }

    private fun getAllCategory() =
        getData(
            getData = { categoryRepository.getAll() },
            getState = { state.value.getAllCategory },
            setState = { newState -> state.update { it.copy(getAllCategory = newState) } },
        )

    private fun getAllProfile() =
        getData(
            getData = { profileRepository.getAll() },
            getState = { state.value.getAllProfile },
            setState = { newState -> state.update { it.copy(getAllProfile = newState) } },
        )


    private fun getAllTasksByDate(event: AllTasksEvent.FilterTasks) {
        state.update {
            it.copy(
                filterOptions = event.filterOptions,
                getAllTasks = PaginationState(),
            )
        }

        getNextTaskPageJobList.forEach { it.cancel() }
        getNextTaskPageJobList.clear()
        getNextTaskPage()
    }

    private fun addEditTask(event: AllTasksEvent.AddEditTask) {
        val newTask = state.value.getAllTasks.data.find {
            it.id == event.task.id
        }

        if (newTask != null) {
            state.update {
                it.copy(
                    getAllTasks = it.getAllTasks.copy(
                        data = it.getAllTasks.data.map { task ->
                            if (task.id == event.task.id)
                                event.task
                            else
                                task
                        }.filter { task ->
                            task.dueDate == state.value.filterOptions.dueDate.toString()
                        },
                    ),
                )
            }

            val getItinerary =
                state.value.sharedTaskState.getAllItineraries.data.orEmpty()
                    .none {
                        it.id == event.task.itineraryService?.itineraryId ||
                            it.id == event.task.itinerary?.id
                    }

            if (getItinerary) {
                if (!event.task.itineraryService?.itineraryId.isNullOrEmpty()) {
                    sharedTaskViewModel.getAllItineraries(
                        listOf(
                            event.task.itineraryService?.itineraryId ?: "",
                        ).toSet(),
                    )
                } else if (!event.task.itinerary?.id.isNullOrEmpty()) {
                    sharedTaskViewModel.getAllItineraries(listOf(event.task.itinerary?.id ?: "").toSet())
                }
            }

        } else {
            // Add task
            event.task.dueDate?.let {
                getAllTasksByDate(
                    AllTasksEvent.FilterTasks(
                        TaskFilterOptions(
                            dueDate = LocalDate.parse(it),
                        ),
                    ),
                )
            }
        }
    }

    private fun updateTaskStatus(event: AllTasksEvent.UpdateTaskStatus) =
        scope.launch {
            val task = state.value.getAllTasks.data
                .find { it.id == event.taskId }

            if (task == null) {
                val overdueTask = state.value.getAllOverDueTasks.data
                    ?.find { it.id == event.taskId }
                    ?.copy(status = event.status) ?: return@launch

                updateTaskUseCase.update(
                    task = overdueTask,
                    onSuccess = {
                        getOverdueTasks()
                    },
                )
            } else {
                updateTaskUseCase.update(
                    task = task.copy(status = event.status),
                )
            }
        }

    private fun editClient(event: AllTasksEvent.EditClient) {
        sharedTaskViewModel.editClient(
            event.client,
            event.itinerary,
        )
    }

    private fun updateTaskOwner(event: AllTasksEvent.UpdateTaskOwner) {
        // Prevent multiple requests for the same task
        if (event.taskId in state.value.updateTaskOwnerLoading) return

        // Check if the new owner is the same as the current owner
        val task = state.value.getAllTasks.data.find { it.id == event.taskId }
        if (task?.owner?.id == event.owner.id) return

        state.value = state.value.copy(
            updateTaskOwnerLoading = state.value.updateTaskOwnerLoading + event.taskId,
        )

        val newTask = TaskModel.empty.copy(
            id = event.taskId,
            owner = event.owner,
        )

        scope.launch {
            taskRepository.update(newTask)
                .onSuccess { task ->
                    val taskList = state.value.getAllTasks.data.update(
                        predicate = { it.id == task.id },
                        update = { task },
                    )

                    state.value = state.value.copy(
                        getAllTasks = state.value.getAllTasks.copy(
                            data = taskList,
                        ),
                        updateTaskOwnerLoading = state.value.updateTaskOwnerLoading - event.taskId,
                    )
                }
                .onFailure {
                    state.value = state.value.copy(
                        updateTaskOwnerLoading = state.value.updateTaskOwnerLoading - event.taskId,
                    )
                }
        }
    }
}